import { IBusinessToolStore } from 'store/useBusinessToolStore/types';

import { getOperatingIndustry } from 'helpers/businessTool/mappers/getOperatingIndustry';
import { hasMeaningfulValues } from 'helpers/hasMeaningfulValues';

import { getFormattedNumber } from './helpers/getFormattedNumber';
import { prepareFullNameWithPosition } from './mappers/prepareFullNameWithPosition';
import { prepareTeamMemberCardsInfo } from './mappers/prepareTeamMemberCardsInfo';
import { IOnePageViewProps } from './types';
import { formatDate, formatMoney, plural } from './utils';

export const getOnePagePreviewData = (store: IBusinessToolStore): IOnePageViewProps['data'] => {
  const { GLOBAL, OVERVIEW, EXECUTIVE_SUMMARY, ABOUT_COMPANY, MANAGEMENT_AND_ORGANIZATION, IMPLEMENTATION_PLAN } =
    store.businessPlanChapters;

  const personnelProfiles = MANAGEMENT_AND_ORGANIZATION.managementTeam.personnelProfiles;

  return {
    annualIncome: GLOBAL.annualIncome ? formatMoney(GLOBAL.annualIncome) : formatMoney(GLOBAL.projectedAnnualIncome),
    companyName: GLOBAL.companyName,
    companyLogo: OVERVIEW.company.file.length ? OVERVIEW.company.file[0].url : null,
    projectBriefDescription: GLOBAL.projectBriefDescription,
    projectPaybackPeriod: GLOBAL.projectPaybackPeriod
      ? `${GLOBAL.projectPaybackPeriod} ${plural(['month', 'months'], Number(GLOBAL.projectPaybackPeriod))}`
      : null,
    forecastedProfitability: GLOBAL.forecastedProfitability
      ? getFormattedNumber({ data: GLOBAL.forecastedProfitability, endSymbol: '%' })
      : null,
    fundingAmount: GLOBAL.fundingAmount ? formatMoney(GLOBAL.fundingAmount) : null,
    totalCost: GLOBAL.totalCost ? formatMoney(GLOBAL.totalCost) : null,
    percentageOfFunding: GLOBAL.percentageOfFunding ? `${GLOBAL.percentageOfFunding}%` : null,
    companyBriefDescription: GLOBAL.companyBriefDescription,
    operatingIndustry: getOperatingIndustry(GLOBAL),
    registrationDate: GLOBAL.registrationDate ? formatDate(GLOBAL.registrationDate) : null,
    numberOfEmployees: GLOBAL.numberOfEmployees,
    mainGoals: GLOBAL.mainGoals,
    strategyForAchievingGoals: ABOUT_COMPANY.missionAndStrategy.howProjectAddressesTheProblem,
    mainProductsOrServices: GLOBAL.mainProductsOrServices,
    competitionAdvantage: GLOBAL.competitionAdvantage,
    targetMarket: GLOBAL.targetMarket,
    mainCompetitors: GLOBAL.mainCompetitors,
    targetChannels: GLOBAL.targetAudience,
    revenueStreams: GLOBAL.revenueStreams,
    marketingStrategy: GLOBAL.marketingStrategy,
    lifeCycleStage: OVERVIEW.company.lifeCycleStages.length ? OVERVIEW.company.lifeCycleStages[0].value : null,
    projectStatus:
      Array.isArray(OVERVIEW.project.status) && OVERVIEW.project.status.length
        ? OVERVIEW.project.status.map(({ value }) => value)
        : null,
    team: EXECUTIVE_SUMMARY.organizationAndTeam.members,
    dynamics: EXECUTIVE_SUMMARY.marketAndCustomers.dynamics,
    problemOfMarket: ABOUT_COMPANY.missionAndStrategy.problemOfMarket,
    projectedAnnualIncome: GLOBAL.projectedAnnualIncome,

    projectedAnnualProfit: EXECUTIVE_SUMMARY.financialInformation.projectedAnnualProfit,

    returnOnInvestment: EXECUTIVE_SUMMARY.financialInformation.returnOnInvestment,
    internalRateOfReturn: EXECUTIVE_SUMMARY.financialInformation.internalRateOfReturn,
    teamMembers: prepareTeamMemberCardsInfo(personnelProfiles),
    contacts: {
      websiteUrl: ABOUT_COMPANY.generalInfo.websiteUrl,
      phone: ABOUT_COMPANY.generalInfo.phone,
      email: ABOUT_COMPANY.generalInfo.email,
      fullNameWithPos: prepareFullNameWithPosition(ABOUT_COMPANY.generalInfo),
      address: GLOBAL.address,
    },
    financingNeedsChart: hasMeaningfulValues(EXECUTIVE_SUMMARY.financingNeeds)
      ? EXECUTIVE_SUMMARY.financingNeeds
      : null,
    implementationPlanStages: hasMeaningfulValues(IMPLEMENTATION_PLAN.stages) ? IMPLEMENTATION_PLAN.stages : null,
  };
};
